import { Box, Button, Flex, Icon, Text, Tooltip } from '@chakra-ui/react';
import { Check, ChevronLeft, ChevronRight } from 'lucide-react';
import { FunctionComponent } from 'react';
import { useNavigate } from 'react-router-dom';
import { PROCESS_STAGES } from '@/constants';
import { useAppSelector } from '@/redux';

const HOME_PATH = '/';

export type StepHeaderProps = {
  currentStep: number;
  isNextEnabled: boolean;
  isBackEnabled: boolean;
  // Tooltip on the disabled Next button.
  nextDisabledReason?: string;
};

/** Inline steps, the selected client/wave, and the only Back/Next controls between steps. */
export const StepHeader: FunctionComponent<StepHeaderProps> = ({
  currentStep,
  isNextEnabled,
  isBackEnabled,
  nextDisabledReason,
}) => {
  const navigate = useNavigate();
  const selectedClient = useAppSelector((state) => state.wave.selectedClient);
  const selectedWaveOption = useAppSelector((state) => state.wave.selectedWaveOption);

  const backPath = PROCESS_STAGES.find((stage) => stage.id === currentStep - 1)?.path ?? HOME_PATH;
  const nextPath = PROCESS_STAGES.find((stage) => stage.id === currentStep + 1)?.path;

  return (
    <Flex
      as="header"
      align="center"
      gap={6}
      px={6}
      py={2}
      minH="52px"
      flexShrink={0}
      wrap="wrap"
      bg="marketcast.base.backgroundAlt"
      borderBottom="1px solid"
      borderColor="marketcast.gray.5"
    >
      <Flex as="nav" aria-label="Wave process steps">
        <Flex as="ol" align="center" gap={2} listStyleType="none" m={0} p={0}>
          {PROCESS_STAGES.map((stage, index) => {
            const isComplete = stage.id < currentStep;
            const isCurrent = stage.id === currentStep;
            const isReached = isComplete || isCurrent;

            return (
              <Flex
                as="li"
                key={stage.id}
                align="center"
                gap={2}
                aria-current={isCurrent ? 'step' : undefined}
              >
                {index > 0 && (
                  <Box
                    aria-hidden
                    w="24px"
                    h="2px"
                    bg={isReached ? 'marketcast.primaryCta.1' : 'marketcast.gray.4'}
                  />
                )}
                <Flex
                  w="22px"
                  h="22px"
                  borderRadius="full"
                  align="center"
                  justify="center"
                  fontSize="11px"
                  fontWeight="700"
                  flexShrink={0}
                  bg={isReached ? 'marketcast.primaryCta.1' : 'marketcast.gray.5'}
                  color={isReached ? 'white' : 'marketcast.gray.2'}
                >
                  {isComplete ? <Icon as={Check} boxSize={3} aria-label="Completed" /> : stage.id}
                </Flex>
                {/* Non-current names hide on narrow screens to keep the bar one line. */}
                <Text
                  fontSize="13px"
                  fontWeight={isCurrent ? '600' : '400'}
                  color={isCurrent ? 'marketcast.text.primary' : 'marketcast.gray.2'}
                  whiteSpace="nowrap"
                  display={isCurrent ? 'block' : { base: 'none', md: 'block' }}
                >
                  {stage.name}
                </Text>
              </Flex>
            );
          })}
        </Flex>
      </Flex>

      <Flex align="center" gap={2} fontSize="13px" minW={0} aria-label="Selected client and wave">
        <Text color="marketcast.gray.2">Client</Text>
        <Text fontWeight="600" color="marketcast.text.primary" noOfLines={1}>
          {selectedClient?.groupName ?? 'Not selected'}
        </Text>
        <Text color="marketcast.gray.4" aria-hidden>
          ·
        </Text>
        <Text color="marketcast.gray.2">Wave</Text>
        <Text fontWeight="600" color="marketcast.text.primary" whiteSpace="nowrap">
          {selectedWaveOption || 'Not selected'}
        </Text>
      </Flex>

      <Flex gap={2} ml="auto">
        <Button variant="outline" onClick={() => navigate(backPath)} isDisabled={!isBackEnabled}>
          <Icon as={ChevronLeft} boxSize={4} mr={1} />
          Back
        </Button>
        {nextPath && (
          // shouldWrapChildren: disabled buttons don't fire hover events, so the span hosts the tooltip.
          <Tooltip
            label={nextDisabledReason}
            isDisabled={isNextEnabled || !nextDisabledReason}
            shouldWrapChildren
          >
            <Button
              variant={isNextEnabled ? 'solid' : 'outline'}
              isDisabled={!isNextEnabled}
              onClick={() => navigate(nextPath)}
            >
              Next
              <Icon as={ChevronRight} boxSize={4} ml={1} />
            </Button>
          </Tooltip>
        )}
      </Flex>
    </Flex>
  );
};
